import type { User } from "firebase/auth";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { type LimitError, limitsChange, limitsForm, type LimitsForm, rangeText, rateEffect } from "./limits";
import { SkeletonSection } from "./Skeleton";

const FIELDS: { key: keyof LimitsForm; label: string; unit: string; hint: string }[] = [
  { key: "nearFull", label: "Near full", unit: "%", hint: "Flag an account when the fill estimate is above this share of the capacity. With no estimate yet, the load from the stated rate." },
  { key: "smallWeeks", label: "Small container", unit: "weeks", hint: "Flag a container that holds fewer weeks than this at the stated rate." },
  { key: "rateBand", label: "Rate flag", unit: "%", hint: "Flag a measured rate this much above or below the stated rate." },
  { key: "highScrap", label: "High scrap", unit: "%", hint: "Flag scrap above this share of the raw gallons." },
  { key: "defaultScrap", label: "Default scrap", unit: "%", hint: "The scrap share of an account with no pickups in the last 13 weeks." },
];

/** Design 8.4: the limits of the flags on the Accounts page, for the whole organization. */
export function ForecastSettings({ user }: { user: User }) {
  const [found, setFound] = useState<api.ForecastLimits | "failed" | undefined>(undefined);
  const [form, setForm] = useState<LimitsForm | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<LimitError | null>(null);
  const [undo, setUndo] = useState<api.ForecastLimits["limits"] | null>(null);
  const status = useRef<HTMLParagraphElement>(null);
  const inputs = useRef<Partial<Record<keyof LimitsForm, HTMLInputElement | null>>>({});

  useEffect(() => {
    if (problem) inputs.current[problem.key]?.focus();
  }, [problem]);

  useEffect(() => {
    user
      .getIdToken()
      .then(api.forecastLimits)
      .then(
        (limits) => {
          setFound(limits);
          setForm(limitsForm(limits.limits));
        },
        () => setFound("failed"),
      );
  }, [user]);

  if (found === undefined || form === null) {
    return found === "failed" ? (
      <fieldset>
        <legend>Forecast limits</legend>
        <p className="error">Cannot load the forecast limits. Try again later.</p>
      </fieldset>
    ) : (
      <SkeletonSection title="Forecast limits" rows={12} />
    );
  }
  if (found === "failed") return null;
  const current = found;
  const send = async (limits: api.ForecastLimits["limits"], done: string, before: api.ForecastLimits["limits"] | null) => {
    setSaving(true);
    setMessage(null);
    setUndo(null);
    try {
      const next = await api.setForecastLimits(await user.getIdToken(), limits);
      setFound(next);
      setForm(limitsForm(next.limits));
      setMessage({ ok: true, text: done });
      setUndo(before);
    } catch (e) {
      const detail = e instanceof api.ApiError && e.status < 500 ? e.detail : undefined;
      setMessage({ ok: false, text: detail ?? "The limits were not saved. Try again." });
    } finally {
      setSaving(false);
      if (before === null) status.current?.focus();
    }
  };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    const limits = limitsChange(form);
    if ("key" in limits) {
      setMessage(null);
      setProblem(limits);
      return;
    }
    setProblem(null);
    await send(limits, "Saved. The Accounts page uses the new limits.", current.limits);
  };
  const defaults = limitsForm(current.defaults);
  return (
    <form onSubmit={save} noValidate data-testid="forecast-limits">
      <fieldset disabled={!found.can_edit || saving}>
        <legend>Forecast limits</legend>
        {FIELDS.map(({ key, label, unit, hint }) => {
          const wrong = problem?.key === key;
          const effect = key === "rateBand" ? rateEffect(form.rateBand) : null;
          return (
            <div key={key} className="limit">
              <label htmlFor={`limit-${key}`}>
                {label} ({unit})
              </label>
              <input
                id={`limit-${key}`}
                ref={(element) => {
                  inputs.current[key] = element;
                }}
                data-testid={`limit-${key}`}
                inputMode="decimal"
                value={form[key]}
                aria-invalid={wrong || undefined}
                aria-describedby={wrong ? `limit-${key}-error limit-${key}-hint` : `limit-${key}-hint`}
                onChange={(e) => {
                  setForm({ ...form, [key]: e.target.value });
                  setMessage(null);
                  setUndo(null);
                  if (wrong) setProblem(null);
                }}
              />
              {wrong && (
                <p id={`limit-${key}-error`} role="alert" className="error" data-testid={`limit-${key}-error`}>
                  {problem?.text}
                </p>
              )}
              <p id={`limit-${key}-hint`} className="hint" aria-live={key === "rateBand" ? "polite" : undefined}>
                {effect ?? hint} {rangeText(key, defaults)}
              </p>
            </div>
          );
        })}
        {found.can_edit && (
          <div className="actions">
            <button className="primary" data-testid="save-limits">
              {saving ? "Saving…" : "Save the limits"}
            </button>
            <button
              type="button"
              data-testid="default-limits"
              onClick={() => {
                setForm(defaults);
                setProblem(null);
                setUndo(null);
                setMessage({ ok: true, text: "The default values are in the form. Press Save the limits to use them." });
              }}
            >
              Use the defaults
            </button>
          </div>
        )}
      </fieldset>
      <p ref={status} tabIndex={-1} role="status" data-testid="limits-status" className={message && !message.ok ? "error" : undefined}>
        {message?.text}
        {undo && found.can_edit && (
          <>
            {" "}
            <button
              type="button"
              data-testid="undo-limits"
              disabled={saving}
              onClick={() => void send(undo, "Undone.", null)}
            >
              Undo
            </button>
          </>
        )}
      </p>
    </form>
  );
}
